import type { LoginCreditOfferContext } from '@/components/auth/login-credit-offer';
import { LoginModal } from '@/components/auth/login-modal';
import { Routes } from '@/lib/routes';
import { useRouter } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';

interface LoginWrapperProps {
  children: React.ReactNode;
  mode?: 'modal' | 'redirect';
  asChild?: boolean;
  callbackUrl?: string;
  creditOffer?: LoginCreditOfferContext;
  description?: string;
  onBeforeOpen?: () => void | Promise<void>;
  onGoogleSignInStart?: () => void;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
  title?: string;
}
/**
 * Wraps content to trigger login
 * - mode="modal" opens a login dialog
 * - mode="redirect" navigates to the login page
 */
export function LoginWrapper({
  children,
  mode = 'redirect',
  asChild,
  callbackUrl,
  creditOffer,
  description,
  onBeforeOpen,
  onGoogleSignInStart,
  onOpenChange,
  onSuccess,
  title,
}: LoginWrapperProps) {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const handleRedirect = async () => {
    await onBeforeOpen?.();
    router.navigate({
      to: Routes.Login,
      search: callbackUrl ? { callbackUrl } : {},
    });
  };
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) void onBeforeOpen?.();
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };
  if (!mounted) {
    return <span>{children}</span>;
  }
  if (mode === 'modal') {
    return (
      <LoginModal
        callbackUrl={callbackUrl}
        creditOffer={creditOffer}
        description={description}
        onGoogleSignInStart={onGoogleSignInStart}
        onOpenChange={handleOpenChange}
        onSuccess={onSuccess}
        open={open}
        title={title}
        trigger={
          asChild && React.isValidElement(children) ? (
            children
          ) : (
            <button type="button">{children}</button>
          )
        }
      />
    );
  }
  return (
    <button type="button" onClick={handleRedirect} className="inline">
      {children}
    </button>
  );
}
